import { useEffect, useRef } from 'react';

export const useGameRenderer = ({
  canvasRef,
  camera,
  setCamera,
  gameData,
  ships,
  vans,
  roadPaths,
  frog,
  animationTime,
  setAnimationTime,
  onCanvasClick
}) => {
  const animationRef = useRef(null);
  const tileCanvasRef = useRef(null); // Cache for base tiles
  
  // Local state for animated vehicles
  const localVehiclesRef = useRef({ ships: new Map(), vans: new Map() });

  // Touch handling refs
  const isTouchingRef = useRef(false);
  const lastTouchPosRef = useRef({ x: 0, y: 0 });
  const initialPinchDistanceRef = useRef(null);
  const initialZoomRef = useRef(camera.zoom);
  const touchStartTimeRef = useRef(0);
  const touchStartPosRef = useRef({ x: 0, y: 0 });

  const TILE_SIZE = 32;

  // Sync local vehicles with props
  useEffect(() => {
    // Sync ships
    const currentShipIds = new Set(ships.map(s => s.id));
    // Remove ships that no longer exist
    for (const id of localVehiclesRef.current.ships.keys()) {
      if (!currentShipIds.has(id)) localVehiclesRef.current.ships.delete(id);
    }
    // Add new ships
    ships.forEach(ship => {
      if (!localVehiclesRef.current.ships.has(ship.id)) {
        if (ship.path && ship.path.length > 0) {
          localVehiclesRef.current.ships.set(ship.id, {
            ...ship,
            x: ship.path[0].x,
            y: ship.path[0].y,
            currentWaypointIndex: 0,
            isReturning: false,
            directionX: 0,
            directionY: 0
          });
        }
      }
    });

    // Sync vans
    const currentVanIds = new Set(vans.map(v => v.id));
    // Remove vans that no longer exist
    for (const id of localVehiclesRef.current.vans.keys()) {
      if (!currentVanIds.has(id)) localVehiclesRef.current.vans.delete(id);
    }
    // Add new vans
    vans.forEach(van => {
      if (!localVehiclesRef.current.vans.has(van.id)) {
        if (van.path && van.path.length > 0) {
          localVehiclesRef.current.vans.set(van.id, {
            ...van,
            x: van.path[0].x,
            y: van.path[0].y,
            currentWaypointIndex: 0,
            isReturning: false,
            directionX: 0,
            directionY: 0
          });
        }
      }
    });
  }, [ships, vans]);

  useEffect(() => {
    if (!gameData.worldData) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { alpha: false }); // Disable alpha for performance

    // Create tile cache canvas
    if (!tileCanvasRef.current) {
      tileCanvasRef.current = document.createElement('canvas');
      tileCanvasRef.current.width = gameData.worldData.width * TILE_SIZE;
      tileCanvasRef.current.height = gameData.worldData.height * TILE_SIZE;
    }

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      drawMap();
    };

    // Pre-calculate and cache colors
    const tileColorCache = new Map();
    const tileBorderColorCache = new Map();

    const getTileColor = (tileType, isOwned, ownerColor) => {
      const cacheKey = `${tileType}-${isOwned}-${ownerColor}`;
      if (tileColorCache.has(cacheKey)) {
        return tileColorCache.get(cacheKey);
      }

      let color;
      if (isOwned && tileType >= 3 && tileType <= 14) {
        color = ownerColor || '#FFD700';
      } else {
        const baseColors = ['#4CAF50', '#2196F3', '#795548', '#E0E0E0', '#E0E0E0', '#E0E0E0', '#E0E0E0', '#E0E0E0', '#E0E0E0', '#E0E0E0', '#E0E0E0', '#E0E0E0', '#E0E0E0', '#E0E0E0', '#E0E0E0'];
        color = baseColors[tileType] || '#4CAF50';
      }
      
      tileColorCache.set(cacheKey, color);
      return color;
    };

    const getTileBorderColor = (tileType, isOwned, ownerColor) => {
      const cacheKey = `${tileType}-${isOwned}-${ownerColor}`;
      if (tileBorderColorCache.has(cacheKey)) {
        return tileBorderColorCache.get(cacheKey);
      }

      let borderColor;
      if (isOwned && tileType >= 3 && tileType <= 14) {
        borderColor = adjustBrightness(ownerColor || '#FFD700', -30);
      } else if (isOwned) {
        borderColor = '#FF8F00';
      } else {
        const baseBorders = ['#2E7D32', '#1976D2', '#5D4037', '#757575', '#757575', '#757575', '#757575', '#757575', '#757575', '#757575', '#757575', '#757575', '#757575', '#757575', '#757575'];
        borderColor = baseBorders[tileType] || '#2E7D32';
      }
      
      tileBorderColorCache.set(cacheKey, borderColor);
      return borderColor;
    };

    const adjustBrightness = (hex, amount) => {
      const num = parseInt(hex.replace("#", ""), 16);
      const amt = Math.round(2.55 * amount);
      const R = Math.min(255, Math.max(0, (num >> 16) + amt));
      const G = Math.min(255, Math.max(0, (num >> 8 & 0x00FF) + amt));
      const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
      return "#" + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
    };

    // Render base tiles to cache (only once)
    const renderBaseTiles = () => {
      const tileCtx = tileCanvasRef.current.getContext('2d', { alpha: false });
      
      // Create noise pattern for texture
      const noiseCanvas = document.createElement('canvas');
      noiseCanvas.width = 128;
      noiseCanvas.height = 128;
      const noiseCtx = noiseCanvas.getContext('2d');
      
      for (let i = 0; i < 4000; i++) {
        const x = Math.random() * 128;
        const y = Math.random() * 128;
        const opacity = Math.random() * 0.05;
        noiseCtx.fillStyle = `rgba(0, 0, 0, ${opacity})`;
        noiseCtx.fillRect(x, y, 1, 1);
        noiseCtx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        noiseCtx.fillRect(x + 1, y + 1, 1, 1);
      }
      const noisePattern = tileCtx.createPattern(noiseCanvas, 'repeat');
      
      // Group tiles by color for batch rendering
      const tilesByColor = new Map();
      
      for (let y = 0; y < gameData.worldData.height; y++) {
        for (let x = 0; x < gameData.worldData.width; x++) {
          const tileIndex = y * gameData.worldData.width + x;
          const tileType = gameData.worldData.tiles[tileIndex];
          const color = getTileColor(tileType, false, null);
          
          if (!tilesByColor.has(color)) {
            tilesByColor.set(color, []);
          }
          tilesByColor.get(color).push({ x: x * TILE_SIZE, y: y * TILE_SIZE });
        }
      }

      // Batch render tiles by color
      tilesByColor.forEach((tiles, color) => {
        tileCtx.fillStyle = color;
        tileCtx.beginPath();
        tiles.forEach(tile => {
          tileCtx.rect(tile.x, tile.y, TILE_SIZE, TILE_SIZE);
        });
        tileCtx.fill();

        // Apply noise texture
        tileCtx.fillStyle = noisePattern;
        tileCtx.beginPath();
        tiles.forEach(tile => {
          tileCtx.rect(tile.x, tile.y, TILE_SIZE, TILE_SIZE);
        });
        tileCtx.fill();
      });

      // Create sand pattern
      const sandPatternCanvas = document.createElement('canvas');
      sandPatternCanvas.width = 8;
      sandPatternCanvas.height = 8;
      const sandPatternCtx = sandPatternCanvas.getContext('2d');
      sandPatternCtx.fillStyle = '#F0E68C'; // Khaki
      sandPatternCtx.fillRect(0, 0, 8, 8);
      sandPatternCtx.fillStyle = '#D2B48C'; // Tan
      for (let i = 0; i < 25; i++) {
        sandPatternCtx.fillRect(Math.floor(Math.random() * 8), Math.floor(Math.random() * 8), 1, 1);
      }
      const sandPattern = tileCtx.createPattern(sandPatternCanvas, 'repeat');

      // Create foam pattern
      const foamPatternCanvas = document.createElement('canvas');
      foamPatternCanvas.width = 32;
      foamPatternCanvas.height = 32;
      const foamPatternCtx = foamPatternCanvas.getContext('2d');
      foamPatternCtx.fillStyle = getTileColor(1, false, null); // base water color
      foamPatternCtx.fillRect(0, 0, 32, 32);
      foamPatternCtx.strokeStyle = '#FFFFFF';
      foamPatternCtx.lineWidth = 1.5;
      for (let i = 0; i < 4; i++) {
        foamPatternCtx.beginPath();
        foamPatternCtx.moveTo(-5, Math.random() * 32);
        foamPatternCtx.bezierCurveTo(10, Math.random() * 32, 22, Math.random() * 32, 37, Math.random() * 32);
        foamPatternCtx.stroke();
      }
      const foamPattern = tileCtx.createPattern(foamPatternCanvas, 'repeat');

      // Draw shoreline details
      for (let y = 0; y < gameData.worldData.height; y++) {
        for (let x = 0; x < gameData.worldData.width; x++) {
          const tileType = gameData.worldData.tiles[y * gameData.worldData.width + x];
          const world = gameData.worldData;

          const getTile = (dx, dy) => {
            const nx = x + dx;
            const ny = y + dy;
            if (nx >= 0 && nx < world.width && ny >= 0 && ny < world.height) {
              return world.tiles[ny * world.width + nx];
            }
            return -1; // Out of bounds
          };

          if (tileType === 0) { // Grass
            const hasWaterTop = getTile(0, -1) === 1;
            const hasWaterBottom = getTile(0, 1) === 1;
            const hasWaterLeft = getTile(-1, 0) === 1;
            const hasWaterRight = getTile(1, 0) === 1;

            if (!hasWaterTop && !hasWaterBottom && !hasWaterLeft && !hasWaterRight) continue;

            tileCtx.fillStyle = sandPattern;
            const sandWidth = 4;

            if (hasWaterTop) tileCtx.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, sandWidth);
            if (hasWaterBottom) tileCtx.fillRect(x * TILE_SIZE, (y + 1) * TILE_SIZE - sandWidth, TILE_SIZE, sandWidth);
            if (hasWaterLeft) tileCtx.fillRect(x * TILE_SIZE, y * TILE_SIZE, sandWidth, TILE_SIZE);
            if (hasWaterRight) tileCtx.fillRect((x + 1) * TILE_SIZE - sandWidth, y * TILE_SIZE, sandWidth, TILE_SIZE);

            const grassColor = getTileColor(0, false, null);
            tileCtx.fillStyle = grassColor;

            if (hasWaterTop && hasWaterLeft && getTile(-1, -1) !== 1) tileCtx.fillRect(x * TILE_SIZE, y * TILE_SIZE, sandWidth, sandWidth);
            if (hasWaterTop && hasWaterRight && getTile(1, -1) !== 1) tileCtx.fillRect((x + 1) * TILE_SIZE - sandWidth, y * TILE_SIZE, sandWidth, sandWidth);
            if (hasWaterBottom && hasWaterLeft && getTile(-1, 1) !== 1) tileCtx.fillRect(x * TILE_SIZE, (y + 1) * TILE_SIZE - sandWidth, sandWidth, sandWidth);
            if (hasWaterBottom && hasWaterRight && getTile(1, 1) !== 1) tileCtx.fillRect((x + 1) * TILE_SIZE - sandWidth, (y + 1) * TILE_SIZE - sandWidth, sandWidth, sandWidth);

          } else if (tileType === 1) { // Water
            const hasGrassTop = getTile(0, -1) === 0;
            const hasGrassBottom = getTile(0, 1) === 0;
            const hasGrassLeft = getTile(-1, 0) === 0;
            const hasGrassRight = getTile(1, 0) === 0;

            if (!hasGrassTop && !hasGrassBottom && !hasGrassLeft && !hasGrassRight) continue;

            tileCtx.fillStyle = foamPattern;
            const foamWidth = 3;

            if (hasGrassTop) tileCtx.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, foamWidth);
            if (hasGrassBottom) tileCtx.fillRect(x * TILE_SIZE, (y + 1) * TILE_SIZE - foamWidth, TILE_SIZE, foamWidth);
            if (hasGrassLeft) tileCtx.fillRect(x * TILE_SIZE, y * TILE_SIZE, foamWidth, TILE_SIZE);
            if (hasGrassRight) tileCtx.fillRect((x + 1) * TILE_SIZE - foamWidth, y * TILE_SIZE, foamWidth, TILE_SIZE);

            const waterColor = getTileColor(1, false, null);
            tileCtx.fillStyle = waterColor;

            if (hasGrassTop && hasGrassLeft && getTile(-1, -1) !== 0) tileCtx.fillRect(x * TILE_SIZE, y * TILE_SIZE, foamWidth, foamWidth);
            if (hasGrassTop && hasGrassRight && getTile(1, -1) !== 0) tileCtx.fillRect((x + 1) * TILE_SIZE - foamWidth, y * TILE_SIZE, foamWidth, foamWidth);
            if (hasGrassBottom && hasGrassLeft && getTile(-1, 1) !== 0) tileCtx.fillRect(x * TILE_SIZE, (y + 1) * TILE_SIZE - foamWidth, foamWidth, foamWidth);
            if (hasGrassBottom && hasGrassRight && getTile(1, 1) !== 0) tileCtx.fillRect((x + 1) * TILE_SIZE - foamWidth, (y + 1) * TILE_SIZE - foamWidth, foamWidth, foamWidth);
          }
        }
      }
    };

    // Render base tiles once
    if (!tileCanvasRef.current.hasRendered) {
      renderBaseTiles();
      tileCanvasRef.current.hasRendered = true;
    }

    // Helper to update vehicle position
    const updateVehicle = (vehicle, deltaTime) => {
      if (!vehicle.path || vehicle.path.length < 2) return;

      // Speed is in tiles per 100ms (from backend logic: 0.1 per 100ms = 1.0 per sec)
      // We want tiles per millisecond for deltaTime
      const speedPerMs = (vehicle.speed || 0.1) * 10 / 1000; 
      const moveDistance = speedPerMs * deltaTime;

      let remainingMove = moveDistance;

      while (remainingMove > 0) {
        const targetIndex = vehicle.isReturning 
          ? vehicle.currentWaypointIndex - 1 
          : vehicle.currentWaypointIndex + 1;

        // Check if we reached end of path
        if (targetIndex < 0 || targetIndex >= vehicle.path.length) {
          vehicle.isReturning = !vehicle.isReturning;
          continue;
        }

        const target = vehicle.path[targetIndex];
        const dx = target.x - vehicle.x;
        const dy = target.y - vehicle.y;
        const distToTarget = Math.sqrt(dx * dx + dy * dy);

        if (distToTarget > 0) {
          vehicle.directionX = dx / distToTarget;
          vehicle.directionY = dy / distToTarget;
        }

        if (remainingMove >= distToTarget) {
          // Reached waypoint
          vehicle.x = target.x;
          vehicle.y = target.y;
          vehicle.currentWaypointIndex = targetIndex;
          remainingMove -= distToTarget;
        } else {
          // Move towards waypoint
          vehicle.x += vehicle.directionX * remainingMove;
          vehicle.y += vehicle.directionY * remainingMove;
          remainingMove = 0;
        }
      }
    };

    const drawShips = () => {
      // Use local animated ships instead of props
      const animatedShips = Array.from(localVehiclesRef.current.ships.values());
      if (animatedShips.length === 0) return;

      animatedShips.forEach(ship => {
        const shipX = ship.x * TILE_SIZE;
        const shipY = ship.y * TILE_SIZE;
        const shipSize = TILE_SIZE * 0.8;

        // Calculate ship rotation based on direction
        const angle = Math.atan2(ship.directionY, ship.directionX);
        
        ctx.save();
        ctx.translate(shipX + TILE_SIZE * 0.5, shipY + TILE_SIZE * 0.5);
        ctx.rotate(angle);

        // Modern ship hull (using owner's color)
        const ownerColor = ship.ownerColor || '#FFFFFF';
        const gradient = ctx.createLinearGradient(0, -shipSize * 0.2, 0, shipSize * 0.2);
        
        // Create a lighter and darker shade of the owner's color for gradient effect
        const lighterColor = adjustBrightness(ownerColor, 40);
        const darkerColor = adjustBrightness(ownerColor, -20);
        
        gradient.addColorStop(0, lighterColor);
        gradient.addColorStop(0.5, ownerColor);
        gradient.addColorStop(1, darkerColor);
        
        ctx.fillStyle = gradient;
        ctx.fillRect(-shipSize * 0.45, -shipSize * 0.18, shipSize * 0.9, shipSize * 0.36);
        
        // Modern superstructure (bridge/command center) - keep blue for contrast
        ctx.fillStyle = '#4A90E2';
        ctx.fillRect(-shipSize * 0.15, -shipSize * 0.25, shipSize * 0.3, shipSize * 0.2);
        
        // Bridge windows
        ctx.fillStyle = '#87CEEB';
        ctx.fillRect(-shipSize * 0.12, -shipSize * 0.22, shipSize * 0.24, shipSize * 0.08);

        // Modern radar/communication mast
        ctx.fillStyle = '#2C5282';
        ctx.fillRect(-shipSize * 0.02, -shipSize * 0.4, shipSize * 0.04, shipSize * 0.2);
        
        // Radar dish
        ctx.fillStyle = '#718096';
        ctx.beginPath();
        ctx.arc(0, -shipSize * 0.35, shipSize * 0.06, 0, 2 * Math.PI);
        ctx.fill();

        // Modern exhaust stack
        ctx.fillStyle = '#2D3748';
        ctx.fillRect(shipSize * 0.1, -shipSize * 0.3, shipSize * 0.08, shipSize * 0.15);

        // Hull accent line (using owner's color)
        ctx.strokeStyle = ownerColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(-shipSize * 0.4, 0);
        ctx.lineTo(shipSize * 0.2, 0);
        ctx.stroke();

        // Modern navigation lights
        ctx.fillStyle = '#22C55E'; // Green starboard light
        ctx.beginPath();
        ctx.arc(shipSize * 0.35, -shipSize * 0.12, shipSize * 0.03, 0, 2 * Math.PI);
        ctx.fill();
        
        ctx.fillStyle = '#EF4444'; // Red port light
        ctx.beginPath();
        ctx.arc(shipSize * 0.35, shipSize * 0.12, shipSize * 0.03, 0, 2 * Math.PI);
        ctx.fill();

        ctx.restore();
      });
    };

    const drawVans = () => {
      // Use local animated vans instead of props
      const animatedVans = Array.from(localVehiclesRef.current.vans.values());
      if (animatedVans.length === 0) return;

      animatedVans.forEach(van => {
        const vanX = van.x * TILE_SIZE;
        const vanY = van.y * TILE_SIZE;
        const vanSize = TILE_SIZE * 0.7;

        // Calculate van rotation based on direction
        const angle = Math.atan2(van.directionY, van.directionX);
        
        ctx.save();
        ctx.translate(vanX + TILE_SIZE * 0.5, vanY + TILE_SIZE * 0.5);
        ctx.rotate(angle);

        // Van body (using owner's color)
        const ownerColor = van.ownerColor || '#FFFFFF';
        const gradient = ctx.createLinearGradient(0, -vanSize * 0.25, 0, vanSize * 0.25);
        
        const lighterColor = adjustBrightness(ownerColor, 30);
        const darkerColor = adjustBrightness(ownerColor, -15);
        
        gradient.addColorStop(0, lighterColor);
        gradient.addColorStop(0.5, ownerColor);
        gradient.addColorStop(1, darkerColor);
        
        ctx.fillStyle = gradient;
        ctx.fillRect(-vanSize * 0.4, -vanSize * 0.2, vanSize * 0.8, vanSize * 0.4);
        
        // Add subtle shadow under van body
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.fillRect(-vanSize * 0.38, vanSize * 0.18, vanSize * 0.76, vanSize * 0.02);
        
        // Van cab (driver section)
        ctx.fillStyle = '#4A90E2';
        ctx.fillRect(vanSize * 0.25, -vanSize * 0.15, vanSize * 0.15, vanSize * 0.3);
        
        // Van windows
        ctx.fillStyle = '#87CEEB';
        ctx.fillRect(vanSize * 0.27, -vanSize * 0.12, vanSize * 0.11, vanSize * 0.08);
        ctx.fillRect(vanSize * 0.27, vanSize * 0.04, vanSize * 0.11, vanSize * 0.08);

        // Front grill (dark gray)
        ctx.fillStyle = '#2C3E50';
        ctx.fillRect(vanSize * 0.38, -vanSize * 0.08, vanSize * 0.02, vanSize * 0.16);
        // Grill lines
        ctx.strokeStyle = '#34495E';
        ctx.lineWidth = 1;
        for (let i = 0; i < 3; i++) {
          ctx.beginPath();
          ctx.moveTo(vanSize * 0.38, -vanSize * 0.06 + i * vanSize * 0.04);
          ctx.lineTo(vanSize * 0.4, -vanSize * 0.06 + i * vanSize * 0.04);
          ctx.stroke();
        }

        // Headlights (yellow glow)
        ctx.fillStyle = '#FFF8DC';
        ctx.beginPath();
        ctx.arc(vanSize * 0.42, -vanSize * 0.1, vanSize * 0.02, 0, 2 * Math.PI);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(vanSize * 0.42, vanSize * 0.1, vanSize * 0.02, 0, 2 * Math.PI);
        ctx.fill();

        // Taillights (red)
        ctx.fillStyle = '#FF4444';
        ctx.beginPath();
        ctx.arc(-vanSize * 0.42, -vanSize * 0.1, vanSize * 0.02, 0, 2 * Math.PI);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(-vanSize * 0.42, vanSize * 0.1, vanSize * 0.02, 0, 2 * Math.PI);
        ctx.fill();

        // Side mirrors (small rectangles)
        ctx.fillStyle = '#2C3E50';
        ctx.fillRect(vanSize * 0.35, -vanSize * 0.18, vanSize * 0.03, vanSize * 0.04);
        ctx.fillRect(vanSize * 0.35, vanSize * 0.14, vanSize * 0.03, vanSize * 0.04);

        ctx.restore();
      });
    };

    const drawRoads = () => {
      if (!roadPaths || Object.keys(roadPaths).length === 0) return;

      Object.entries(roadPaths).forEach(([roadKey, waypoints]) => {
        if (!waypoints || waypoints.length < 2) return;

        ctx.save();

        // First draw the black road base
        ctx.strokeStyle = '#2C2C2C'; // Dark asphalt color
        ctx.lineWidth = 6 / camera.zoom; // Slightly wider for the base
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Draw the main road path
        ctx.beginPath();
        ctx.moveTo(waypoints[0].x * TILE_SIZE + TILE_SIZE / 2, waypoints[0].y * TILE_SIZE + TILE_SIZE / 2);
        
        for (let i = 1; i < waypoints.length; i++) {
          ctx.lineTo(waypoints[i].x * TILE_SIZE + TILE_SIZE / 2, waypoints[i].y * TILE_SIZE + TILE_SIZE / 2);
        }
        
        ctx.stroke();

        // Draw white dashed center line (only when zoomed in enough to see details)
        if (camera.zoom >= 0.5) {
          ctx.strokeStyle = '#FFFFFF'; // White color for road markings
          ctx.lineWidth = 1.5 / camera.zoom; // Thinner line for markings
          ctx.setLineDash([8 / camera.zoom, 6 / camera.zoom]); // Dashed line pattern scaled with zoom
          ctx.lineCap = 'butt'; // Sharp ends for dashes

          // Draw the dashed center line
          ctx.beginPath();
          ctx.moveTo(waypoints[0].x * TILE_SIZE + TILE_SIZE / 2, waypoints[0].y * TILE_SIZE + TILE_SIZE / 2);
          
          for (let i = 1; i < waypoints.length; i++) {
            ctx.lineTo(waypoints[i].x * TILE_SIZE + TILE_SIZE / 2, waypoints[i].y * TILE_SIZE + TILE_SIZE / 2);
          }
          
          ctx.stroke();
          
          // Reset line dash for other drawing operations
          ctx.setLineDash([]);
        }

        // Add road edge lines for more realism (when zoomed in)
        if (camera.zoom >= 0.8) {
          ctx.strokeStyle = '#404040'; // Slightly lighter gray for road edges
          ctx.lineWidth = 0.8 / camera.zoom;
          ctx.lineCap = 'round';

          // Calculate perpendicular offset for road edges
          const roadWidth = 3 / camera.zoom;

          for (let i = 0; i < waypoints.length - 1; i++) {
            const x1 = waypoints[i].x * TILE_SIZE + TILE_SIZE / 2;
            const y1 = waypoints[i].y * TILE_SIZE + TILE_SIZE / 2;
            const x2 = waypoints[i + 1].x * TILE_SIZE + TILE_SIZE / 2;
            const y2 = waypoints[i + 1].y * TILE_SIZE + TILE_SIZE / 2;

            // Calculate perpendicular direction for road edges
            const dx = x2 - x1;
            const dy = y2 - y1;
            const length = Math.sqrt(dx * dx + dy * dy);
            
            if (length > 0) {
              const perpX = (-dy / length) * roadWidth;
              const perpY = (dx / length) * roadWidth;

              // Draw left edge
              ctx.beginPath();
              ctx.moveTo(x1 + perpX, y1 + perpY);
              ctx.lineTo(x2 + perpX, y2 + perpY);
              ctx.stroke();

              // Draw right edge
              ctx.beginPath();
              ctx.moveTo(x1 - perpX, y1 - perpY);
              ctx.lineTo(x2 - perpX, y2 - perpY);
              ctx.stroke();
            }
          }
        }

        // Add small road markers for visibility when zoomed out
        if (camera.zoom < 0.5) {
          ctx.fillStyle = '#404040'; // Dark gray for markers
          waypoints.forEach((waypoint, index) => {
            if (index % 3 === 0) { // Every third waypoint to avoid clutter
              const markerSize = 1.5 / camera.zoom;
              ctx.beginPath();
              ctx.arc(
                waypoint.x * TILE_SIZE + TILE_SIZE / 2, 
                waypoint.y * TILE_SIZE + TILE_SIZE / 2, 
                markerSize, 
                0, 
                2 * Math.PI
              );
              ctx.fill();
            }
          });
        }

        ctx.restore();
      });
    };

    const drawFrog = () => {
      if (!frog) return;

      const frogX = frog.x * TILE_SIZE;
      const frogY = frog.y * TILE_SIZE;
      const frogSize = TILE_SIZE * 1.2; // Slightly larger than a tile

      ctx.save();
      ctx.translate(frogX, frogY);

      // Calculate jump animation offset and leg movement
      let jumpOffset = 0;
      let legExtension = 0;
      let legAngle = 0; // New: angle for leg rotation during jump
      if (frog.isJumping) {
        // Create a parabolic jump arc
        const jumpProgress = frog.jumpProgress || 0; // 0 to 1
        jumpOffset = -Math.sin(jumpProgress * Math.PI) * frogSize * 0.5;
        
        // Realistic leg movement:
        // - During ascent (0 to 0.5): legs move upward and extend
        // - During descent (0.5 to 1): legs move downward to prepare for landing
        if (jumpProgress < 0.5) {
          // Ascending: legs push up and extend
          const ascendProgress = jumpProgress * 2; // 0 to 1 during first half
          legExtension = ascendProgress * 0.3; // Legs extend
          legAngle = ascendProgress * 0.4; // Legs rotate upward
        } else {
          // Descending: legs prepare for landing
          const descendProgress = (jumpProgress - 0.5) * 2; // 0 to 1 during second half
          legExtension = (1 - descendProgress) * 0.3; // Legs retract
          legAngle = (1 - descendProgress) * 0.4 - descendProgress * 0.3; // Legs rotate downward
        }
      }

      // Apply jump offset
      ctx.translate(0, jumpOffset);

      // Budgett's frog has a very flat, wide build
      const bodyWidth = frogSize * 0.9;
      const bodyHeight = frogSize * 0.45;

      // Draw shadow only when jumping (removed when sitting)
      if (frog.isJumping) {
        const shadowOpacity = 0.2;
        const shadowSize = 0.5;
        ctx.fillStyle = `rgba(0, 0, 0, ${shadowOpacity})`;
        ctx.beginPath();
        ctx.ellipse(0, bodyHeight * 0.8 - jumpOffset, bodyWidth * shadowSize, bodyHeight * 0.4, 0, 0, 2 * Math.PI);
        ctx.fill();
      }

      // Body (flat and wide with textured appearance)
      const bodyGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, bodyWidth / 2);
      bodyGradient.addColorStop(0, '#A0C5A0'); // Lighter sage green
      bodyGradient.addColorStop(0.5, '#7AA77A'); // Medium green
      bodyGradient.addColorStop(0.8, '#6B8E6B'); // Darker green
      bodyGradient.addColorStop(1, '#556B55'); // Even darker edges

      ctx.fillStyle = bodyGradient;
      ctx.beginPath();
      ctx.ellipse(0, 0, bodyWidth / 2, bodyHeight / 2, 0, 0, 2 * Math.PI);
      ctx.fill();

      // Add some skin texture (darker spots/bumps)
      ctx.fillStyle = 'rgba(85, 107, 85, 0.5)';
      const spotPositions = [
        { x: -0.25, y: -0.1 }, { x: 0.2, y: -0.15 }, { x: -0.15, y: 0.1 },
        { x: 0.15, y: 0.05 }, { x: -0.3, y: 0.15 }, { x: 0.3, y: -0.05 }
      ];
      spotPositions.forEach(pos => {
        ctx.beginPath();
        ctx.arc(pos.x * bodyWidth, pos.y * bodyHeight, frogSize * 0.07, 0, 2 * Math.PI);
        ctx.fill();
      });

      // Add lighter spots for more texture
      ctx.fillStyle = 'rgba(160, 197, 160, 0.3)';
      const lightSpots = [
        { x: -0.1, y: -0.2 }, { x: 0.25, y: 0.1 }, { x: -0.2, y: 0 }
      ];
      lightSpots.forEach(pos => {
        ctx.beginPath();
        ctx.arc(pos.x * bodyWidth, pos.y * bodyHeight, frogSize * 0.06, 0, 2 * Math.PI);
        ctx.fill();
      });

      // Front legs (animated during jump with reduced extension)
      const frontLegExtend = legExtension * 0.6;
      ctx.fillStyle = '#7AA77A';
      
      // Left front leg
      ctx.save();
      ctx.translate(-bodyWidth * 0.38, bodyHeight * 0.15);
      ctx.rotate(-0.2 - legAngle * 0.5 - frontLegExtend);
      ctx.fillRect(-frogSize * 0.12, -frogSize * 0.06, frogSize * 0.15, frogSize * 0.1);
      // Left front hand
      ctx.translate(-frogSize * 0.12, 0);
      ctx.fillRect(-frogSize * 0.08, -frogSize * 0.04, frogSize * 0.08, frogSize * 0.08);
      ctx.restore();

      // Right front leg
      ctx.save();
      ctx.translate(bodyWidth * 0.38, bodyHeight * 0.15);
      ctx.rotate(0.2 + legAngle * 0.5 + frontLegExtend);
      ctx.fillRect(-frogSize * 0.03, -frogSize * 0.06, frogSize * 0.15, frogSize * 0.1);
      // Right front hand
      ctx.translate(frogSize * 0.12, 0);
      ctx.fillRect(0, -frogSize * 0.04, frogSize * 0.08, frogSize * 0.08);
      ctx.restore();

      // Eyes (large and bulging on top of head) - characteristic of Budgett's frog
      const eyeY = -bodyHeight * 0.35;
      const eyeSpacing = bodyWidth * 0.28;

      // Eye ridges/brow (for grumpy look)
      ctx.strokeStyle = '#6B8E6B';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      
      ctx.beginPath();
      ctx.moveTo(-eyeSpacing - frogSize * 0.12, eyeY - frogSize * 0.05);
      ctx.lineTo(-eyeSpacing + frogSize * 0.12, eyeY - frogSize * 0.08);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(eyeSpacing - frogSize * 0.12, eyeY - frogSize * 0.08);
      ctx.lineTo(eyeSpacing + frogSize * 0.12, eyeY - frogSize * 0.05);
      ctx.stroke();

      // Left eye
      ctx.fillStyle = '#D4AF70'; // Goldish brown eye color
      ctx.beginPath();
      ctx.ellipse(-eyeSpacing, eyeY, frogSize * 0.16, frogSize * 0.14, 0, 0, 2 * Math.PI);
      ctx.fill();

      // Right eye
      ctx.beginPath();
      ctx.ellipse(eyeSpacing, eyeY, frogSize * 0.16, frogSize * 0.14, 0, 0, 2 * Math.PI);
      ctx.fill();

      // Horizontal pupils (characteristic of Budgett's frog)
      ctx.fillStyle = '#000000';
      ctx.beginPath();
      ctx.ellipse(-eyeSpacing, eyeY, frogSize * 0.1, frogSize * 0.04, 0, 0, 2 * Math.PI);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(eyeSpacing, eyeY, frogSize * 0.1, frogSize * 0.04, 0, 0, 2 * Math.PI);
      ctx.fill();

      // Eye highlights for realism
      ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.beginPath();
      ctx.arc(-eyeSpacing - frogSize * 0.04, eyeY - frogSize * 0.03, frogSize * 0.04, 0, 2 * Math.PI);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(eyeSpacing - frogSize * 0.04, eyeY - frogSize * 0.03, frogSize * 0.04, 0, 2 * Math.PI);
      ctx.fill();

      // Mouth (wide and grumpy - Budgett's frogs have a very wide mouth)
      ctx.strokeStyle = '#4A5A4A';
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      // More pronounced grumpy frown
      ctx.moveTo(-bodyWidth * 0.4, bodyHeight * 0.05);
      ctx.quadraticCurveTo(-bodyWidth * 0.2, bodyHeight * 0.25, 0, bodyHeight * 0.2);
      ctx.quadraticCurveTo(bodyWidth * 0.2, bodyHeight * 0.25, bodyWidth * 0.4, bodyHeight * 0.05);
      ctx.stroke();

      // Nostrils (small bumps on snout)
      ctx.fillStyle = '#556B55';
      ctx.beginPath();
      ctx.arc(-frogSize * 0.08, bodyHeight * 0.05, frogSize * 0.025, 0, 2 * Math.PI);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(frogSize * 0.08, bodyHeight * 0.05, frogSize * 0.025, 0, 2 * Math.PI);
      ctx.fill();

      ctx.restore();
    };

    const drawMap = () => {
      // Clear with sky background
      ctx.fillStyle = '#87CEEB';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Calculate visible area
      const scaledTileSize = TILE_SIZE * camera.zoom;
      const startTileX = Math.max(0, Math.floor(camera.x / scaledTileSize));
      const endTileX = Math.min(gameData.worldData.width - 1, Math.ceil((camera.x + canvas.width) / scaledTileSize));
      const startTileY = Math.max(0, Math.floor(camera.y / scaledTileSize));
      const endTileY = Math.min(gameData.worldData.height - 1, Math.ceil((camera.y + canvas.height) / scaledTileSize));

      ctx.save();
      ctx.translate(-camera.x, -camera.y);
      ctx.scale(camera.zoom, camera.zoom);

      // Draw cached base tiles (visible area only)
      const sx = startTileX * TILE_SIZE;
      const sy = startTileY * TILE_SIZE;
      const sw = (endTileX - startTileX + 1) * TILE_SIZE;
      const sh = (endTileY - startTileY + 1) * TILE_SIZE;
      
      ctx.drawImage(tileCanvasRef.current, sx, sy, sw, sh, sx, sy, sw, sh);
      
      // Draw ownership layer directly (not cached)
      Object.entries(gameData.tileOwners).forEach(([tileKey, ownership]) => {
        const [x, y] = tileKey.split(',').map(Number);
        
        // Only draw if tile is visible
        if (x >= startTileX && x <= endTileX && y >= startTileY && y <= endTileY) {
          const tileIndex = y * gameData.worldData.width + x;
          const tileType = gameData.worldData.tiles[tileIndex];
          const ownerColor = ownership.ownerColor || getPlayerColor(ownership.owner);
          
          const fillColor = getTileColor(tileType, true, ownerColor);
          const borderColor = getTileBorderColor(tileType, true, ownerColor);
          
          // Draw owned tile
          ctx.fillStyle = fillColor;
          ctx.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
          
          // Add 3D bevel effect for buildings
          if (tileType >= 3) {
            ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
            ctx.beginPath();
            ctx.moveTo(x * TILE_SIZE, y * TILE_SIZE + TILE_SIZE);
            ctx.lineTo(x * TILE_SIZE, y * TILE_SIZE);
            ctx.lineTo(x * TILE_SIZE + TILE_SIZE, y * TILE_SIZE);
            ctx.lineTo(x * TILE_SIZE + TILE_SIZE - 4, y * TILE_SIZE + 4);
            ctx.lineTo(x * TILE_SIZE + 4, y * TILE_SIZE + 4);
            ctx.lineTo(x * TILE_SIZE + 4, y * TILE_SIZE + TILE_SIZE - 4);
            ctx.fill();
            
            ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
            ctx.beginPath();
            ctx.moveTo(x * TILE_SIZE + TILE_SIZE, y * TILE_SIZE);
            ctx.lineTo(x * TILE_SIZE + TILE_SIZE, y * TILE_SIZE + TILE_SIZE);
            ctx.lineTo(x * TILE_SIZE, y * TILE_SIZE + TILE_SIZE);
            ctx.lineTo(x * TILE_SIZE + 4, y * TILE_SIZE + TILE_SIZE - 4);
            ctx.lineTo(x * TILE_SIZE + TILE_SIZE - 4, y * TILE_SIZE + TILE_SIZE - 4);
            ctx.lineTo(x * TILE_SIZE + TILE_SIZE - 4, y * TILE_SIZE + 4);
            ctx.fill();
          }
          
          // Draw border
          ctx.strokeStyle = borderColor;
          ctx.lineWidth = 2;
          ctx.strokeRect(x * TILE_SIZE + 1, y * TILE_SIZE + 1, TILE_SIZE - 2, TILE_SIZE - 2);
        }
      });

      // Draw auction highlights (only if auctions exist and visible)
      const shouldDrawIcons = true; // Changed to always draw icons
      const activeAuctions = gameData.activeAuctions || {};
      
      if (Object.keys(activeAuctions).length > 0) {
        const pulseIntensity = (Math.sin(animationTime) + 1) / 2;
        const auctionTiles = [];
        
        Object.entries(activeAuctions).forEach(([tileKey, auction]) => {
          const [x, y] = tileKey.split(',').map(Number);
          if (x >= startTileX && x <= endTileX && y >= startTileY && y <= endTileY) {
            auctionTiles.push({ x: x * TILE_SIZE, y: y * TILE_SIZE });
          }
        });

        if (auctionTiles.length > 0) {
          // Pulsing fill
          const r = 255, g = Math.round(87 + 65 * pulseIntensity), b = 34;
          ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
          ctx.beginPath();
          auctionTiles.forEach(tile => {
            ctx.rect(tile.x, tile.y, TILE_SIZE, TILE_SIZE);
          });
          ctx.fill();

          // Pulsing border
          const pulseWidth = 2 + pulseIntensity * 3;
          const pulseOpacity = 0.6 + pulseIntensity * 0.4;
          ctx.strokeStyle = `rgba(255, 152, 0, ${pulseOpacity})`;
          ctx.lineWidth = pulseWidth / camera.zoom;
          ctx.beginPath();
          auctionTiles.forEach(tile => {
            ctx.rect(tile.x + 1, tile.y + 1, TILE_SIZE - 2, TILE_SIZE - 2);
          });
          ctx.stroke();
        }
      }

      // Draw icons only when zoomed in
      if (shouldDrawIcons) {
        const iconMap = {
          3: '🌾', 4: '🏪', 5: '⛏️', 6: '🐟', 7: '⚓', 8: '🛢️', 9: '🕳️',
          10: '🏗️', 11: '⚡', 12: '🏭', 13: '🏦', 14: '🏢'
        };
        
        const iconSize = TILE_SIZE * 0.6;
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.font = `${iconSize * 0.8}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Add shadow for better visibility
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = 3;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
        
        for (let x = startTileX; x <= endTileX; x++) {
          for (let y = startTileY; y <= endTileY; y++) {
            const tileIndex = y * gameData.worldData.width + x;
            const tileType = gameData.worldData.tiles[tileIndex];
            const icon = iconMap[tileType];
            
            if (icon) {
              const centerX = x * TILE_SIZE + TILE_SIZE / 2;
              const centerY = y * TILE_SIZE + TILE_SIZE / 2;
              ctx.fillText(icon, centerX, centerY);
            }
          }
        }
        
        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }

      drawRoads();
      drawShips();
      drawVans();
      drawFrog();

      ctx.restore();
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Add wheel event listener with passive: false to allow preventDefault
    const handleWheel = (e) => {
      e.preventDefault();
      
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      // Calculate world coordinates before zoom
      const worldX = (mouseX + camera.x) / camera.zoom;
      const worldY = (mouseY + camera.y) / camera.zoom;
      
      const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.1, Math.min(3, camera.zoom * zoomFactor));
      
      // Calculate new camera position to keep the world point under the mouse
      const newCameraX = worldX * newZoom - mouseX;
      const newCameraY = worldY * newZoom - mouseY;

      // Update camera state
      setCamera({
        x: newCameraX,
        y: newCameraY,
        zoom: newZoom
      });
    };
    canvas.addEventListener('wheel', handleWheel, { passive: false });

    const handleTouchStart = (e) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        isTouchingRef.current = true;
        lastTouchPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        touchStartTimeRef.current = Date.now();
        touchStartPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else if (e.touches.length === 2) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        initialPinchDistanceRef.current = Math.sqrt(
          (touch2.clientX - touch1.clientX) ** 2 + (touch2.clientY - touch1.clientY) ** 2
        );
        initialZoomRef.current = camera.zoom;
      }
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      if (e.touches.length === 1 && isTouchingRef.current) {
        const deltaX = e.touches[0].clientX - lastTouchPosRef.current.x;
        const deltaY = e.touches[0].clientY - lastTouchPosRef.current.y;

        setCamera(prev => ({
          ...prev,
          x: prev.x - deltaX,
          y: prev.y - deltaY
        }));

        lastTouchPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else if (e.touches.length === 2) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const currentDistance = Math.sqrt(
          (touch2.clientX - touch1.clientX) ** 2 + (touch2.clientY - touch1.clientY) ** 2
        );

        if (initialPinchDistanceRef.current) {
          const zoomFactor = currentDistance / initialPinchDistanceRef.current;
          const newZoom = Math.max(0.1, Math.min(3, initialZoomRef.current * zoomFactor));

          // Calculate center of touches for zoom focus
          const centerX = (touch1.clientX + touch2.clientX) / 2;
          const centerY = (touch1.clientY + touch2.clientY) / 2;
          const rect = canvas.getBoundingClientRect();
          const mouseX = centerX - rect.left;
          const mouseY = centerY - rect.top;

          const worldX = (mouseX + camera.x) / camera.zoom;
          const worldY = (mouseY + camera.y) / camera.zoom;

          const newCameraX = worldX * newZoom - mouseX;
          const newCameraY = worldY * newZoom - mouseY;

          setCamera({
            x: newCameraX,
            y: newCameraY,
            zoom: newZoom
          });
        }
      }
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      if (e.changedTouches.length === 1) {
        const touch = e.changedTouches[0];
        const duration = Date.now() - touchStartTimeRef.current;
        const distance = Math.sqrt(
          (touch.clientX - touchStartPosRef.current.x) ** 2 + (touch.clientY - touchStartPosRef.current.y) ** 2
        );

        if (duration < 300 && distance < 10) {
          // It's a tap, simulate click
          const fakeEvent = {
            clientX: touch.clientX,
            clientY: touch.clientY,
            preventDefault: () => {}
          };
          onCanvasClick(fakeEvent);
        }
      }

      if (e.touches.length === 0) {
        isTouchingRef.current = false;
        initialPinchDistanceRef.current = null;
      }
    };

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });

    const startAnimation = () => {
      if (animationRef.current) return;
      
      let lastTime = performance.now();
      const targetFPS = 60;
      const frameTime = 1000 / targetFPS;
      
      const animate = (currentTime) => {
        const deltaTime = currentTime - lastTime;
        
        if (deltaTime >= frameTime) {
          setAnimationTime(prev => (prev + 0.05) % (Math.PI * 2));
          
          // Update vehicle positions
          localVehiclesRef.current.ships.forEach(ship => updateVehicle(ship, deltaTime));
          localVehiclesRef.current.vans.forEach(van => updateVehicle(van, deltaTime));

          drawMap();
          lastTime = currentTime - (deltaTime % frameTime);
        }
        
        animationRef.current = requestAnimationFrame(animate);
      };
      animationRef.current = requestAnimationFrame(animate);
    };
    
    startAnimation();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [camera, setCamera, gameData.worldData, gameData.tileOwners, gameData.activeAuctions, ships, vans, roadPaths, frog, animationTime]);

  // Helper function to get player color (moved from parent)
  const getPlayerColor = (playerId) => {
    // This assumes gameData.players is passed; if not, you might need to pass it or handle differently
    const player = gameData.players?.find(p => p.userId === playerId);
    return player?.color || '#FFFFFF';
  };

  const centerCameraOnTile = (tileX, tileY) => {
    const zoom = camera.zoom;
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    const worldX = tileX * TILE_SIZE + TILE_SIZE / 2;
    const worldY = tileY * TILE_SIZE + TILE_SIZE / 2;
    
    setCamera({
      x: worldX * zoom - screenWidth / 2,
      y: worldY * zoom - screenHeight / 2,
      zoom: zoom
    });
  };

  return { centerCameraOnTile };
};